import { BaseCartridge, RomImage } from './base';

/**
 * Mapper 4 — MMC3 (TxROM).
 *
 *   0x8000 even  bank select: bits 0-2 target register, bit 6 PRG mode, bit 7 CHR A12 inversion
 *   0x8001 odd   bank data -> R0..R7 (R0/R1: 2KB CHR; R2-R5: 1KB CHR; R6/R7: 8KB PRG)
 *   0xA000 even  mirroring (0=vertical, 1=horizontal; four-screen header wins)
 *   0xA001 odd   PRG-RAM protect (tracked, enforcement best-effort)
 *   0xC000 even  IRQ latch
 *   0xC001 odd   IRQ reload (counter reloads from latch at next clock)
 *   0xE000 even  IRQ disable + acknowledge
 *   0xE001 odd   IRQ enable
 *
 * The IRQ counter is clocked by notifyScanline() — the PPU calls it once per
 * rendered scanline at the A12-rise point. irqPending() exposes the IRQ line;
 * nes.ts polls it every instruction and calls cpu.triggerIRQ().
 */
export class Mmc3Cartridge extends BaseCartridge {
    private bankSelect = 0;
    private readonly banks: number[] = [0, 0, 0, 0, 0, 0, 0, 0];
    private prgRamEnabled = true;

    private irqLatch = 0;
    private irqCounter = 0;
    private irqEnabled = false;
    private irqAsserted = false;

    constructor(image: RomImage) {
        super(image);
    }

    private get prgMode(): number { return (this.bankSelect >> 6) & 1; }
    private get chrInversion(): number { return (this.bankSelect >> 7) & 1; }

    cpuRead(address: number): number {
        const ram = { value: 0 };
        if (this.prgRamEnabled && this.readPrgRam(address, ram)) return ram.value;
        if (address < 0x8000) return 0;

        const banks8 = Math.max(1, this.prgRom.length / 0x2000);
        const last = banks8 - 1;
        const secondLast = banks8 - 2;
        const slot = (address - 0x8000) >> 13; // 0..3 for 0x8000/0xA000/0xC000/0xE000
        let bank: number;
        if (this.prgMode === 0) {
            bank = [this.banks[6], this.banks[7], secondLast, last][slot];
        } else {
            bank = [secondLast, this.banks[7], this.banks[6], last][slot];
        }
        return this.readPrg8(bank, address);
    }

    cpuWrite(address: number, value: number): void {
        if (this.prgRamEnabled && this.writePrgRam(address, value)) return;
        if (address < 0x8000) return;

        const even = (address & 1) === 0;
        if (address < 0xA000) {
            if (even) {
                this.bankSelect = value;
            } else {
                this.banks[this.bankSelect & 0x07] = value; // raw value; wraps on read
            }
        } else if (address < 0xC000) {
            if (even) {
                if (this.mirroring !== 'four-screen') {
                    this.mirroring = (value & 1) ? 'horizontal' : 'vertical';
                }
            } else {
                // PRG-RAM protect: bit 7 enable, bit 6 write-protect (best-effort)
                this.prgRamEnabled = (value & 0x80) !== 0;
            }
        } else if (address < 0xE000) {
            if (even) {
                this.irqLatch = value;
            } else {
                this.irqCounter = 0; // reload from latch on next clock
            }
        } else {
            if (even) {
                this.irqEnabled = false;
                this.irqAsserted = false;
            } else {
                this.irqEnabled = true;
            }
        }
    }

    /** CHR mapping in 1KB slots; R0/R1 are 2KB (low bit ignored). */
    private chrSlot(address: number): number {
        const slot = (address & 0x1FFF) >> 10; // 0..7
        let bank: number;
        const b = this.banks;
        if (this.chrInversion === 0) {
            bank = [b[0] & 0xFE, (b[0] & 0xFE) + 1, b[1] & 0xFE, (b[1] & 0xFE) + 1, b[2], b[3], b[4], b[5]][slot];
        } else {
            bank = [b[2], b[3], b[4], b[5], b[0] & 0xFE, (b[0] & 0xFE) + 1, b[1] & 0xFE, (b[1] & 0xFE) + 1][slot];
        }
        return bank;
    }

    ppuRead(address: number): number {
        return this.readChr1(this.chrSlot(address), address);
    }

    ppuWrite(address: number, value: number): void {
        this.writeChr1(this.chrSlot(address), address, value);
    }

    /** PPU A12-rise hook: clock the scanline IRQ counter. */
    notifyScanline(): void {
        if (this.irqCounter === 0) {
            this.irqCounter = this.irqLatch;
        } else {
            this.irqCounter--;
        }
        if (this.irqCounter === 0 && this.irqEnabled) {
            this.irqAsserted = true;
        }
    }

    irqPending(): boolean {
        return this.irqAsserted;
    }
}
